import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Loading,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  GripVertical,
  Loader2,
  MapPin,
  Plus,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { Attraction } from '@/config/objects/attractions';
import { GenerateAttractionImagesDialog } from '@/components/attractions/GenerateAttractionImagesDialog';
import {
  GENERATION_TIMEOUT_MESSAGE,
  useAttractionImageGeneration,
} from '@/hooks/useAttractionImageGeneration';
import {
  useTourAttractions,
  useUpdateTourAttractionOrder,
} from '@/hooks/useTourAttractionOrder';
import { TourAttractionThemeCell } from '@/components/tours/TourAttractionThemeCell';
import { cn } from '@/lib/utils';

interface TourAttractionsSortableProps {
  tourId: string;
  /**
   * The greater area the parent tour is in. Used to scope the "Add attraction"
   * picker to the same area so admins don't accidentally pin an attraction
   * from a different city.
   */
  greaterAreaId?: string;
  /**
   * Render the stops without add/remove/reorder controls. Used for
   * user-generated tours, which the API protects from internal modification
   * (every mutation would 403).
   */
  readOnly?: boolean;
}

interface StopThumbnailProps {
  tourId: string;
  attraction: Attraction;
}

/**
 * A stop's photo thumbnail with an inline "generate main photo with AI"
 * affordance, mirroring {@link AttractionImageCell} on the attraction list.
 *
 * The Sparkles button overlays the (small) thumbnail on hover/focus and opens
 * the canonical reference-photo dialog. Once generation is kicked off the
 * thumbnail shows a spinner and the shared {@link useAttractionImageGeneration}
 * hook polls the tour's stops list until this attraction's featured image
 * changes (success) or a timeout elapses (a dismissible error badge).
 */
function StopThumbnail({ tourId, attraction }: StopThumbnailProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const currentImageId = attraction.featuredImage?.id ?? null;
  const { isGenerating, hasTimedOut, startGeneration, dismissTimeout } =
    useAttractionImageGeneration({
      currentImageId,
      // Primary: the stops list (`useTourAttractions`) so this row refetches.
      // Also nudge the broader attraction caches (list / detail) to converge.
      invalidateKeys: [['TourAttractionOrder', tourId], ['Attraction']],
    });

  const openDialog = (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger the row's view navigation.
    setDialogOpen(true);
  };

  const dismissError = (e: React.MouseEvent) => {
    e.stopPropagation();
    dismissTimeout();
  };

  return (
    <div className="group relative h-10 w-14 shrink-0">
      {attraction.featuredImage?.url ? (
        <img
          src={attraction.featuredImage.url}
          alt={attraction.name}
          className="h-full w-full rounded-md object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center rounded-md bg-muted">
          <MapPin className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      {!isGenerating && !hasTimedOut && (
        <button
          type="button"
          onClick={openDialog}
          aria-label="Generate main photo with AI"
          title="Generate with AI"
          className={cn(
            'absolute inset-0 flex items-center justify-center rounded-md',
            'bg-black/60 text-white opacity-0 transition-opacity',
            'group-hover:opacity-100 focus-visible:opacity-100',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <Sparkles className="h-4 w-4" />
        </button>
      )}

      {isGenerating && (
        <div
          role="status"
          aria-label="Generating photo"
          className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-black/60"
        >
          <Loader2 className="h-4 w-4 animate-spin text-white" />
        </div>
      )}

      {hasTimedOut && (
        <button
          type="button"
          onClick={dismissError}
          aria-label={GENERATION_TIMEOUT_MESSAGE}
          title={GENERATION_TIMEOUT_MESSAGE}
          className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-destructive/70 text-white"
        >
          <AlertTriangle className="h-4 w-4" />
        </button>
      )}

      <GenerateAttractionImagesDialog
        attractionId={attraction.id}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onGenerated={startGeneration}
      />
    </div>
  );
}

interface SortableAttractionRowProps {
  tourId: string;
  attraction: Attraction;
  position: number;
  readOnly: boolean;
  onRemove: (attractionId: string) => void;
  onView: (attractionId: string) => void;
}

function SortableAttractionRow({
  tourId,
  attraction,
  position,
  readOnly,
  onRemove,
  onView,
}: SortableAttractionRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: attraction.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2 shadow-sm"
    >
      {!readOnly && (
        <button
          type="button"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
          className="cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
        >
          <GripVertical className="h-5 w-5" />
        </button>
      )}

      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600">
        <span className="text-sm font-semibold">{position + 1}</span>
      </div>

      <StopThumbnail tourId={tourId} attraction={attraction} />

      <button
        type="button"
        onClick={() => onView(attraction.id)}
        className="min-w-0 flex-1 text-left"
      >
        <div className="truncate text-sm font-medium hover:underline">
          {attraction.name}
        </div>
        {attraction.neighborhood?.name && (
          <div className="truncate text-xs text-muted-foreground">
            {attraction.neighborhood.name}
          </div>
        )}
      </button>

      <div className="hidden shrink-0 sm:block">
        <TourAttractionThemeCell
          tourId={tourId}
          attractionId={attraction.id}
          currentThemeId={attraction.tourAttraction?.attractionThemeId}
          currentThemeName={
            attraction.tourAttraction?.attractionTheme?.themeType?.name
          }
          themes={attraction.themes}
        />
      </div>

      {!readOnly && (
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Remove ${attraction.name} from tour`}
          onClick={() => onRemove(attraction.id)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

interface AddAttractionDialogProps {
  tourId: string;
  greaterAreaId?: string;
  pinnedIds: Set<string>;
  open: boolean;
  onClose: () => void;
  onAdd: (attraction: Attraction) => Promise<void>;
}

/**
 * Dialog listing attractions in the same greater area not yet on the tour.
 * Mirrors the pattern from `HotelTopPicksList`'s AddPickDialog. Tapping an
 * entry adds it to the tour at the bottom of the order.
 */
function AddAttractionDialog({
  tourId,
  greaterAreaId,
  pinnedIds,
  open,
  onClose,
  onAdd,
}: AddAttractionDialogProps) {
  const apiClient = useApiClient();
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  const { data, isLoading } = useQuery<{ data: Attraction[] }>({
    queryKey: ['TourAttractionPicker', tourId, greaterAreaId],
    enabled: open,
    queryFn: () =>
      apiClient.get<{ data: Attraction[] }>(
        `/attractions?limit=200${
          greaterAreaId ? `&where[greaterAreaId][equals]=${greaterAreaId}` : ''
        }`,
      ),
  });

  const candidates = useMemo(() => {
    const all = (data?.data ?? []).filter((a) => !pinnedIds.has(a.id));
    const term = search.trim().toLowerCase();
    if (!term) return all;
    return all.filter((a) => a.name?.toLowerCase().includes(term));
  }, [data?.data, pinnedIds, search]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[80vh] flex-col overflow-hidden p-0 sm:max-w-[600px]">
        <DialogHeader className="border-b px-6 pb-4 pt-6">
          <DialogTitle>Add attraction to tour</DialogTitle>
          <DialogDescription>
            Pick an attraction in the same area. New stops land at the bottom
            of the order — drag to reorder.
          </DialogDescription>
          <div className="relative mt-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search attractions..."
              className="w-full rounded-md border bg-background py-2 pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-6 py-4">
          {isLoading && <Loading text="Loading attractions..." size="sm" />}
          {!isLoading && candidates.length === 0 && (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              {search.trim()
                ? `No attractions match "${search.trim()}".`
                : 'No more attractions to add.'}
            </div>
          )}
          {candidates.map((attraction) => (
            <button
              key={attraction.id}
              type="button"
              className="flex w-full items-center gap-3 rounded-md border p-3 text-left hover:bg-accent"
              onClick={async () => {
                await onAdd(attraction);
              }}
            >
              {attraction.featuredImage?.url ? (
                <img
                  src={attraction.featuredImage.url}
                  alt={attraction.name}
                  className="h-10 w-14 shrink-0 rounded object-cover"
                />
              ) : (
                <div className="flex h-10 w-14 shrink-0 items-center justify-center rounded bg-muted">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="truncate text-sm font-medium">
                  {attraction.name}
                </div>
                {attraction.neighborhood?.name && (
                  <div className="truncate text-xs text-muted-foreground">
                    {attraction.neighborhood.name}
                  </div>
                )}
              </div>
              <Plus className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
        </div>

        <DialogFooter className="border-t px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Drag-and-drop list of a tour's attractions. The order persists regardless
 * of `pathMode`, but the mobile app only walks the tour in this exact order
 * when `tour.pathMode === 'defined'` — when `'fastest'` we render an inline
 * banner so the admin knows their order isn't user-facing yet.
 */
export function TourAttractionsSortable({
  tourId,
  greaterAreaId,
  readOnly = false,
}: TourAttractionsSortableProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useTourAttractions(tourId);
  const orderMutation = useUpdateTourAttractionOrder(tourId);
  const [addOpen, setAddOpen] = useState(false);

  const attractions = useMemo(() => data?.data ?? [], [data?.data]);
  const pinnedIds = useMemo(
    () => new Set(attractions.map((a) => a.id)),
    [attractions],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = attractions.findIndex((a) => a.id === active.id);
    const newIndex = attractions.findIndex((a) => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(attractions, oldIndex, newIndex);
    orderMutation.mutate({
      attractionIds: reordered.map((a) => a.id),
      optimisticAttractions: reordered.map((a, i) => ({
        ...a,
        tourAttraction: a.tourAttraction
          ? { ...a.tourAttraction, order: i }
          : { id: '', order: i, attractionThemeId: null },
      })),
    });
  };

  const handleRemove = async (attractionId: string) => {
    try {
      await apiClient.delete(`/tours/${tourId}/attractions/${attractionId}`);
      toast.success('Attraction removed from tour');
      // Refetch the ordered list and any other consumers.
      queryClient.invalidateQueries({ queryKey: ['TourAttractionOrder', tourId] });
      queryClient.invalidateQueries({ queryKey: ['Tour', tourId] });
    } catch (error) {
      // Surface the API's reason (e.g. "Cannot modify user-generated tours")
      // instead of a generic failure — support can't act on a blind toast.
      toast.error(
        error instanceof Error && error.message
          ? `Failed to remove attraction: ${error.message}`
          : 'Failed to remove attraction from tour',
      );
    }
  };

  const handleAdd = async (attraction: Attraction) => {
    try {
      await apiClient.post(`/tours/${tourId}/attractions`, {
        attractionId: attraction.id,
      });
      toast.success('Attraction added to tour');
      queryClient.invalidateQueries({ queryKey: ['TourAttractionOrder', tourId] });
      queryClient.invalidateQueries({ queryKey: ['Tour', tourId] });
      setAddOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? `Failed to add attraction: ${error.message}`
          : 'Failed to add attraction to tour',
      );
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Stops</h3>
          <p className="text-sm text-muted-foreground">
            {readOnly
              ? 'Stops are shown in the order guests visit them.'
              : `Drag to reorder. Stop ${attractions.length > 0 ? '1' : ''} is the
            first attraction guests visit.`}
          </p>
        </div>
        {!readOnly && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" />
            Add attraction
          </Button>
        )}
      </div>

      {isLoading && <Loading size="sm" />}

      {!isLoading && attractions.length === 0 && (
        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          No attractions on this tour yet. Add one to get started.
        </div>
      )}

      {!isLoading && attractions.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={attractions.map((a) => a.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {attractions.map((attraction, index) => (
                <SortableAttractionRow
                  key={attraction.id}
                  tourId={tourId}
                  attraction={attraction}
                  position={index}
                  readOnly={readOnly}
                  onRemove={handleRemove}
                  onView={(id) => navigate(`/attractions/${id}`)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {!readOnly && (
        <AddAttractionDialog
          tourId={tourId}
          greaterAreaId={greaterAreaId}
          pinnedIds={pinnedIds}
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onAdd={handleAdd}
        />
      )}
    </div>
  );
}
